import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { InlineLogo, SunIcon } from "../brand/icons";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/exhibitions", label: "Exhibitions" },
  { to: "/contact", label: "Contact" },
];

export const Header = () => {
  const [visible, setVisible] = useState(false);
  const [open, setOpen] = useState(false);
  const lastY = useRef(0);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    lastY.current = window.scrollY;
    const onScroll = () => {
      const hero = document.querySelector("[data-hero]");
      const heroBottom = hero ? hero.offsetTop + hero.offsetHeight : 0;
      const y = window.scrollY;
      if (hero && y < heroBottom - 90) {
        setVisible(false);
      } else if (y < lastY.current - 4) {
        setVisible(true);
      } else if (y > lastY.current + 4 && y > 80) {
        setVisible(false);
      } else if (!hero) {
        setVisible(true);
      }
      lastY.current = y;
    };
    const t = setTimeout(onScroll, 80);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      clearTimeout(t);
      window.removeEventListener("scroll", onScroll);
    };
  }, [location.pathname]);

  const show = open || visible;

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
          show ? "translate-y-0 opacity-100" : "pointer-events-none -translate-y-full opacity-0"
        } ${open ? "bg-transparent text-charcoal" : "hairline-b bg-offwhite text-charcoal"}`}
      >
        <div className="flex items-center justify-between px-5 py-4 lg:px-8">
          <Link to="/" data-testid="header-logo-link" aria-label="Pantheon Art — home" className="relative z-50">
            <InlineLogo />
          </Link>
          <nav className="hidden items-center gap-8 md:flex" aria-label="Primary">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase()}-link`}
                className="link-arrow text-xs"
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
          <button
            type="button"
            data-testid="mobile-menu-toggle"
            aria-expanded={open}
            aria-label={open ? "Close menu" : "Open menu"}
            onClick={() => setOpen((v) => !v)}
            className="relative z-50 flex h-10 w-10 items-center justify-center md:hidden"
          >
            <span className="relative block h-3 w-6">
              <span
                className={`absolute left-0 top-0 block h-px w-6 bg-current transition-transform duration-300 ${
                  open ? "translate-y-[5.5px] rotate-45" : ""
                }`}
              />
              <span
                className={`absolute bottom-0 left-0 block h-px w-6 bg-current transition-transform duration-300 ${
                  open ? "-translate-y-[5.5px] -rotate-45" : ""
                }`}
              />
            </span>
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu-overlay"
            className="fixed inset-0 z-40 flex flex-col justify-center bg-offwhite px-8 text-charcoal md:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
          >
            <nav className="flex flex-col gap-6" aria-label="Mobile">
              {NAV.map((item, i) => (
                <motion.div
                  key={item.to}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.08, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  <NavLink
                    to={item.to}
                    data-testid={`mobile-nav-${item.label.toLowerCase()}-link`}
                    className="h-condensed text-5xl"
                  >
                    {item.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <motion.div
              className="absolute bottom-10 left-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              <SunIcon className="h-8 w-8" />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
